import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

// Wraps raw Supabase PostgrestError into a proper JS Error for Sentry
function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (err && typeof err === 'object' && 'message' in err) {
    const pgErr = err as { message: string; code?: string; details?: string; hint?: string };
    const e = new Error(`[DB ${pgErr.code ?? 'error'}] ${pgErr.message}`);
    e.name = 'PostgrestError';
    (e as any).details = pgErr.details;
    (e as any).hint = pgErr.hint;
    (e as any).code = pgErr.code;
    return e;
  }
  return new Error(String(err));
}

// The single JobPart shape for the app. part_cost is what the company paid (internal);
// sell_price is what the customer is charged. jobs.total_parts_cost / total_parts_sell
// are maintained by the update_job_parts_cost trigger inside the same statement as each
// insert/update/delete below, so callers only need to refetch the job.
export interface JobPart {
  id: string;
  job_id: string;
  part_name: string;
  part_cost: number;
  sell_price: number;
  quantity: number;
  notes?: string;
  created_at: string;
  part_source?: 'company_stock' | 'tech_personal';
}

function invalidateJob(queryClient: ReturnType<typeof useQueryClient>, jobId: string) {
  queryClient.invalidateQueries({ queryKey: ['job-parts', jobId] });
  queryClient.invalidateQueries({ queryKey: ['job', jobId] });
  queryClient.invalidateQueries({ queryKey: ['jobs'] });
  queryClient.invalidateQueries({ queryKey: ['reports', 'overall-report'] });
}

export function useJobParts(jobId: string | null) {
  return useQuery({
    queryKey: ['job-parts', jobId],
    queryFn: async () => {
      if (!jobId) return [];

      const { data, error } = await supabase
        .from('job_parts')
        .select('*')
        .eq('job_id', jobId)
        .order('created_at', { ascending: false });

      if (error) throw toError(error);
      return data as JobPart[];
    },
    enabled: !!jobId,
  });
}

export function useAddJobPart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (partData: {
      job_id: string;
      part_name: string;
      part_cost: number;
      sell_price: number;
      quantity: number;
      notes?: string;
      part_source?: 'company_stock' | 'tech_personal';
    }) => {
      const { error } = await supabase.from('job_parts').insert([partData as any]);
      if (error) throw toError(error);
    },
    onSuccess: (_, variables) => {
      invalidateJob(queryClient, variables.job_id);
      toast({ title: 'Part added successfully' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to add part',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateJobPart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      partId,
      jobId,
      part_cost,
      sell_price,
    }: {
      partId: string;
      jobId: string;
      part_cost?: number;
      sell_price?: number;
    }) => {
      const patch: { part_cost?: number; sell_price?: number } = {};
      if (part_cost !== undefined) patch.part_cost = part_cost;
      if (sell_price !== undefined) patch.sell_price = sell_price;
      if (Object.keys(patch).length === 0) return jobId;

      const { error } = await supabase
        .from('job_parts')
        .update(patch)
        .eq('id', partId);

      if (error) throw toError(error);
      return jobId;
    },
    onSuccess: (jobId) => {
      invalidateJob(queryClient, jobId);
      toast({ title: 'Part updated' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update part',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteJobPart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ partId, jobId }: { partId: string; jobId: string }) => {
      const { error } = await supabase.from('job_parts').delete().eq('id', partId);
      if (error) throw toError(error);
      return jobId;
    },
    onSuccess: (jobId) => {
      invalidateJob(queryClient, jobId);
      toast({ title: 'Part deleted successfully' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete part',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
